/**
 * Iris Plan Selector
 * Replaces variant selector with digital plan selection
 * Adds both physical device and digital plan to cart separately
 * Automatically includes extended warranty for 2 and 3 year plans
 */

class IrisPlanSelector {
  constructor() {
    console.log('=== Iris Plan Selector Initialized ===');
    this.selectedPlanVariantId = null;
    this.selectedPlanYears = null;
    this.selectedDeviceVariantId = null; // Add this back!
    this.physicalProductVariants = []; // Add this back!
    this.plans = [];
    this.warranties = [];
    this.collectionHandle = 'exo-iris-plans';
    this.warrantyCollectionHandle = 'warranty';

    this.softwareRenewal = {
      variantId: 52272536977592,  // Software Renewal variant ID
      // Selling plan IDs for each plan duration (from Appstle)
      sellingPlans: {
        1: 691742212280,  // 1-Year Plan - 100% off for 1 year, then full price
        2: 691743948984,  // 2-Year Plan - 100% off for 2 years, then full price
        3: 691743981752   // 3-Year Plan - 100% off for 3 years, then full price
      }
    };

    this.init();
  }

  async init() {
    console.log('Init called, document ready state:', document.readyState);
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  async setup() {
    console.log('Setup started');
    
    // Check if we're on a product page - try multiple selectors
    let productForm = document.querySelector('form[action*="/cart/add"]');
    
    // If not found, try to find product-form element
    if (!productForm) {
      const productFormElement = document.querySelector('product-form');
      if (productFormElement) {
        productForm = productFormElement.querySelector('form');
      }
    }
    
    console.log('Product form found:', !!productForm);
    console.log('Product form element:', productForm);
    
    if (!productForm) {
      console.log('❌ Not on a product page - exiting');
      return;
    }

    // Get product handle and fetch all variants
    const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
    console.log('Product handle:', productHandle);

    if (productHandle) {
      await this.fetchProductVariants(productHandle);
    }

    // Find variant picker location
    const variantPicker = document.querySelector('.variant-picker, variant-selects, .product-form__input--dropdown');
    console.log('Variant picker found:', !!variantPicker);
    if (!variantPicker) {
      console.log('❌ Variant picker not found - exiting');
      return;
    }

    console.log('✓ Creating plan selector UI');

    // Hide the price element (usually shows $3,799.00)
    const priceElements = document.querySelectorAll('.price, .product__info-wrapper .price, .price--large');
    priceElements.forEach(priceEl => {
      console.log('Hiding price element:', priceEl);
      priceEl.style.display = 'none';
    });

    // Hide any Plan variant selector fieldsets or radio buttons
    const fieldsets = document.querySelectorAll('fieldset, .product-form__input');
    fieldsets.forEach(fieldset => {
      const legend = fieldset.querySelector('legend');
      if (legend && legend.textContent.toLowerCase().includes('plan')) {
        console.log('Hiding plan fieldset:', fieldset);
        fieldset.style.display = 'none';
      }
    });

    // Create wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'iris-plan-selector-wrapper';
    variantPicker.parentNode.insertBefore(wrapper, variantPicker);
    wrapper.appendChild(variantPicker);

    // Create plans container
    const plansContainer = document.createElement('div');
    plansContainer.className = 'iris-plans-container';
    plansContainer.innerHTML = '<div class="iris-plan-loading">Loading plans...</div>';
    wrapper.insertBefore(plansContainer, variantPicker);

    // Create error message container
    const errorContainer = document.createElement('div');
    errorContainer.className = 'iris-plan-error';
    errorContainer.textContent = 'Please select a plan before adding to cart';
    wrapper.appendChild(errorContainer);

    console.log('✓ UI elements created, now fetching plans and warranties...');

    // Fetch plans and warranties
    await this.fetchWarranties();
    await this.fetchPlans();
    this.renderPlans(plansContainer);

    // Intercept add to cart button
    this.interceptAddToCart(productForm, errorContainer);
    
    console.log('=== Setup Complete ===');
  }

  async fetchProductVariants(productHandle) {
    try {
      console.log(`Fetching product variants for: ${productHandle}`);
      const response = await fetch(`/products/${productHandle}.js`);
      
      if (!response.ok) {
        console.error('Failed to fetch product:', response.status);
        return;
      }

      const productData = await response.json();
      this.physicalProductVariants = productData.variants || [];
      
      console.log('✓ Loaded variants:', this.physicalProductVariants.length);
      this.physicalProductVariants.forEach(variant => {
        console.log(`  - ${variant.title || variant.option1}: ID ${variant.id}, Price ${(variant.price / 100).toFixed(2)}`);
      });
    } catch (error) {
      console.error('Error fetching variants:', error);
    }
  }

  async fetchWarranties() {
    const url = `/collections/${this.warrantyCollectionHandle}/products.json`;
    console.log('Fetching warranties from:', url);
    
    try {
      const response = await fetch(url);
      console.log('Warranty response status:', response.status);
      console.log('Warranty response OK:', response.ok);
      
      if (!response.ok) {
        console.error('❌ Failed to fetch warranty collection. Status:', response.status);
        const text = await response.text();
        console.error('Response text:', text);
        return;
      }

      const data = await response.json();
      console.log('✓ Warranty collection data received:', data);
      console.log('Number of products in the warranty collection:', data.products?.length || 0);
      
      if (!data.products || data.products.length === 0) {
        console.error('❌ No products found in the warranty collection');
        return;
      }

      // Map warranty products
      this.warranties = data.products.map(product => {
        // const yearMatch = product.title.match(/(\d+)\s*year/i);
        // const years = yearMatch ? parseInt(yearMatch[1]) : 0;
        let years = 0;
        if (product.id === 10122467934392) years = 2;  // 1-year warranty → for 2-year plan
        if (product.id === 10122524950712) years = 3;  // 2-year warranty → for 3-year plan

        const warranty = {
          id: product.id,
          title: product.title,
          years: years,
          price: parseFloat(product.variants[0].price),
          variantId: product.variants[0].id,
          handle: product.handle
        };
        
        console.log('Mapped warranty:', warranty);
        return warranty;
      });
      
      console.log('✓ Total warranties processed:', this.warranties.length);

    } catch (error) {
      console.error('❌ Error fetching warranties:', error);
      console.error('Error details:', error.message, error.stack);
    }
  }

  async fetchPlans() {
    const url = `/collections/${this.collectionHandle}/products.json`;
    console.log('Fetching plans from:', url);
    
    try {
      const response = await fetch(url);
      console.log('Plans response status:', response.status);
      console.log('Plans response OK:', response.ok);
      
      if (!response.ok) {
        console.error('❌ Failed to fetch plans collection. Status:', response.status);
        const text = await response.text();
        console.error('Response text:', text);
        return;
      }
      
      const data = await response.json();
      console.log('✓ Plans collection data received:', data);
      console.log('Number of products in collection:', data.products?.length || 0);
      
      if (!data.products || data.products.length === 0) {
        console.error('❌ No products found in collection');
        return;
      }

      // Log each product
      data.products.forEach((product, index) => {
        console.log(`Plan Product ${index + 1}:`, {
          title: product.title,
          variants: product.variants?.length,
          firstVariantPrice: product.variants?.[0]?.price,
          firstVariantId: product.variants?.[0]?.id
        });
      });
      
      // Sort plans by price (1 year, 2 year, 3 year)
      this.plans = data.products
        .map(product => {
          const mapped = {
            id: product.id,
            title: product.title,
            price: product.variants[0].price,
            variantId: product.variants[0].id,
            handle: product.handle
          };
          console.log('Mapped plan:', mapped);
          return mapped;
        })
        .sort((a, b) => {
          const priceA = parseFloat(a.price);
          const priceB = parseFloat(b.price);
          console.log(`Sorting: ${a.title} ($${priceA}) vs ${b.title} ($${priceB})`);
          return priceA - priceB;
        });
      
      console.log('✓ Final plans array:', this.plans);
      console.log(`✓ Total plans processed: ${this.plans.length}`);

    } catch (error) {
      console.error('❌ Error fetching plans:', error);
      console.error('Error details:', error.message, error.stack);
    }
  }

  renderPlans(container) {
    console.log('renderPlans called with', this.plans.length, 'plans');
    
    if (this.plans.length === 0) {
      console.error('❌ No plans to render');
      container.innerHTML = `
        <div class="iris-plan-loading" style="color: #dc2626;">
          <p>Unable to load plans.</p>
          <p style="font-size: 12px; color: #6b7280;">Check browser console (F12) for details</p>
        </div>
      `;
      return;
    }

    console.log('✓ Rendering plans...');
    container.innerHTML = '';

    this.plans.forEach((plan, index) => {
      console.log(`Creating plan element ${index + 1}:`, plan);
      const planElement = this.createPlanElement(plan, index);
      container.appendChild(planElement);
    });

    console.log('✓ All plan elements added to DOM');

    // Auto-select first plan
    const firstPlan = container.querySelector('.iris-plan-option');
    if (firstPlan) {
      console.log('✓ Auto-selecting first plan');
      firstPlan.click();
    } else {
      console.error('❌ Could not find first plan to auto-select');
    }
  }

  createPlanElement(plan, index) {
    const planDiv = document.createElement('div');
    planDiv.className = 'iris-plan-option';
    planDiv.dataset.variantId = plan.variantId;
    planDiv.dataset.planIndex = index;

    // Determine plan details based on title
    const yearMatch = plan.title.match(/(\d+)\s*year/i);
    const years = yearMatch ? parseInt(yearMatch[1]) : index + 1;
    
    // Store years on the element for later use
    planDiv.dataset.years = years;
    
    // Find the matching device variant for this plan
    let deviceVariant = null;
    
    // Try to match by variant title containing the year
    deviceVariant = this.physicalProductVariants.find(v => {
      const title = (v.title || v.option1 || '').toLowerCase();
      return title.includes(`${years} year`);
    });
    
    // Fallback: match by index if we have exactly 3 variants
    if (!deviceVariant && this.physicalProductVariants.length === 3) {
      deviceVariant = this.physicalProductVariants[years - 1];
    }
    
    // Last resort: use first variant
    if (!deviceVariant && this.physicalProductVariants.length > 0) {
      deviceVariant = this.physicalProductVariants[0];
      console.warn(`⚠️ Could not find matching variant for ${years} year plan, using first variant`);
    }
    
    console.log(`Plan ${years} year matched to device variant:`, deviceVariant?.id);
    
    // Store device variant ID on the plan element
    planDiv.dataset.deviceVariantId = deviceVariant?.id;
    
    // Prices in Shopify API are strings in format "1200.00" (already in dollars)
    const planPrice = parseFloat(plan.price);
    const devicePrice = deviceVariant ? (deviceVariant.price / 100) : 3799;
    
    // Add warranty price if applicable (2 or 3 years)
    let warrantyPrice = 0;
    if (years >= 2) {
      const warranty = this.warranties.find(w => w.years === years);
      if (warranty) {
        warrantyPrice = warranty.price;
        console.log(`Adding warranty for ${years} year plan: ${warrantyPrice}`);
      } else {
        console.warn(`⚠️ No ${years} year warranty found`);
      }
    }
    
    const totalPrice = (planPrice + devicePrice + warrantyPrice).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    console.log(`Plan ${index + 1} details:`, {
      title: plan.title,
      years,
      planPrice,
      devicePrice,
      warrantyPrice,
      totalPrice,
      planVariantId: plan.variantId,
      deviceVariantId: deviceVariant?.id
    });

    planDiv.innerHTML = `
      <div class="iris-plan-checkmark"></div>
      <div class="iris-plan-header">
        <h3 class="iris-plan-name">${years}-Year Plan</h3>
        <div class="iris-plan-price">${totalPrice}</div>
      </div>
      <div class="iris-plan-details">
        <div class="iris-plan-detail-item">Warranty coverage</div>
        <div class="iris-plan-detail-item">AI software</div>
        <div class="iris-plan-detail-item">Exo Works Essential Software</div>
        <div class="iris-plan-detail-item-subtitle"> Required to use Exo Iris. </div>
      </div>
    `;

    planDiv.addEventListener('click', () => this.selectPlan(planDiv));

    return planDiv;
  }

  selectPlan(planElement) {
    console.log('Plan selected:', planElement.dataset.variantId);
    
    // Remove selection from all plans
    document.querySelectorAll('.iris-plan-option').forEach(el => {
      el.classList.remove('selected');
    });

    // Select clicked plan
    planElement.classList.add('selected');
    this.selectedPlanVariantId = planElement.dataset.variantId;
    this.selectedPlanYears = parseInt(planElement.dataset.years);
    this.selectedDeviceVariantId = planElement.dataset.deviceVariantId; // Get the correct device variant!
    
    console.log('✓ Selected plan variant ID:', this.selectedPlanVariantId);
    console.log('✓ Selected plan years:', this.selectedPlanYears);
    console.log('✓ Selected device variant ID:', this.selectedDeviceVariantId);

    // Hide error message
    const errorContainer = document.querySelector('.iris-plan-error');
    if (errorContainer) {
      errorContainer.classList.remove('show');
    }
  }

  showCustomAlert(message, isError = false) {
    // Remove existing alert if any
    const existingAlert = document.getElementById('iris-custom-alert');
    if (existingAlert) existingAlert.remove();

    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.id = 'iris-custom-alert';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    `;

    // Create modal content
    const modal = document.createElement('div');
    modal.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 32px;
      max-width: 440px;
      width: 90%;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    `;

    modal.innerHTML = `
      <div style="display: flex; align-items: flex-start; gap: 16px;">
        <div style="flex: 1;">
          <h3 style="margin: 0 0 12px 0; font-size: 18px; color: #111; display: flex; align-items: center; gap: 8px;">
            <img src="https://cdn.shopify.com/s/files/1/0778/3802/4888/files/info_15238131.png?v=1762545509" alt="" style="width: 25px; height: 25px;" />
            Checkout limit reached
          </h3>
          <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #666;">
            You can order up to 5 devices per transaction. For 6 or more devices, please contact our sales team at
            <a href="mailto:salesoperations@exo.inc" style="color: #24B0FF; text-decoration: underline;">salesoperations@exo.inc</a>
          </p>
        </div>
      </div>
      <div style="margin-top: 24px; display: flex; gap: 12px; justify-content: flex-end;">
        <button id="iris-alert-ok" style="
          background:rgb(255, 255, 255);
          color:rgba(98, 103, 110, 0.69);
          border:1px solid rgba(98, 103, 110, 0.69);
          border-radius: 15px;
          padding: 10px 24px;
          font-size: 13px;
          cursor: pointer;
        ">OK</button>

        <button id="iris-alert-cart" style="
          background: rgb(255, 255, 255);
          color: #24B0FF;
          border:1px solid #24B0FF;
          border-radius: 15px;
          padding: 10px 24px;
          font-size: 13px;
          cursor: pointer;
        ">Go to Cart</button>
    </div>
    `;

    overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Close on OK button click
  const okButton = document.getElementById('iris-alert-ok');
  okButton.addEventListener('click', () => overlay.remove());

  // Go to cart on Cart button click
  const cartButton = document.getElementById('iris-alert-cart');
  cartButton.addEventListener('click', () => {
    window.location.href = 'https://store.exo.inc/cart';
  });

  // Close on overlay click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  }

  interceptAddToCart(form, errorContainer) {
    console.log('Setting up add to cart interception');
    console.log('Form element:', form);
    
    // Try multiple ways to find the add to cart button
    let addToCartBtn = form.querySelector('[name="add"]') || 
                       form.querySelector('button[type="submit"]') ||
                       form.querySelector('.product-form__submit') ||
                       document.querySelector('.product-form__submit') ||
                       document.querySelector('button[name="add"]');
    
    console.log('Add to cart button found:', !!addToCartBtn);
    console.log('Button element:', addToCartBtn);
    
    if (!addToCartBtn) {
      console.error('❌ Could not find add to cart button');
      console.log('Available buttons in form:', form.querySelectorAll('button'));
      return;
    }

    form.onsubmit = async (e) => {
      console.log('=== Add to Cart Clicked ===');
      console.log('Event:', e);
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      console.log('Selected plan variant ID:', this.selectedPlanVariantId);
      console.log('Selected plan years:', this.selectedPlanYears);
      console.log('Selected device variant ID:', this.selectedDeviceVariantId);

      // Check if plan is selected
      if (!this.selectedPlanVariantId || !this.selectedDeviceVariantId) {
        console.error('❌ No plan selected');
        errorContainer.classList.add('show');
        errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return false;
      }

      // Disable button and show loading state
      const originalBtnText = addToCartBtn.textContent;
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = 'Adding...';
      console.log('Button disabled, showing loading state');

      try {
        console.log('Attempting to add items to cart...');
        await this.addToCart();

        console.log('✓ Successfully added to cart');
        addToCartBtn.textContent = 'Added!';
        
        // Trigger cart drawer/page refresh
        console.log('Redirecting to cart...');
        setTimeout(() => {
          window.location.href = '/cart';
        }, 500);

      } catch (error) {
        console.error('❌ Error adding to cart:', error);
        console.error('Error details:', error.message, error.stack);
        if (error.message === 'QUANTITY_LIMIT') {
          this.showCustomAlert('quantity_limit');
        } else {
          alert('There was an error adding items to cart. Please try again.');
        }
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = originalBtnText;
      }

      return false;
    };
    
    // Also add click event listener to button as backup
    addToCartBtn.addEventListener('click', async (e) => {
      console.log('=== Button Click Intercepted ===');
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Trigger the form submit handler
      if (form.onsubmit) {
        form.onsubmit(e);
      }
    }, true); // Use capture phase
    
    console.log('✓ Add to cart interception set up');
  }

  async addToCart() {
    const items = [];

    // 1. Add physical device WITH THE CORRECT VARIANT
    if (this.selectedDeviceVariantId) {
      items.push({
        id: this.selectedDeviceVariantId, // Use the variant matching the plan years!
        quantity: 1
      });
      console.log('✓ Added physical device to cart request:', this.selectedDeviceVariantId);
    }

    // 2. Add digital plan
    if (this.selectedPlanVariantId) {
      items.push({
        id: this.selectedPlanVariantId,
        quantity: 1
      });
      console.log('✓ Added digital plan to cart request:', this.selectedPlanVariantId);
    }

    // 3. Add warranty if 2 or 3 year plan
    if (this.selectedPlanYears >= 2) {
      const warranty = this.warranties.find(w => w.years === this.selectedPlanYears);
      if (warranty) {
        items.push({
          id: warranty.variantId,
          quantity: 1
        });
        console.log(`✓ Added ${this.selectedPlanYears} year warranty to cart request:`, warranty.variantId, `(${warranty.price})`);
      } else {
        console.warn(`⚠️ No warranty found for ${this.selectedPlanYears} year plan`);
      }
    }

    // 4. Add Software Renewal with selling plan
    const sellingPlanId = this.softwareRenewal.sellingPlans[this.selectedPlanYears];
    if (sellingPlanId) {
      items.push({
        id: this.softwareRenewal.variantId,
        quantity: 1,
        selling_plan: sellingPlanId  // This applies the subscription with discount
      });
      console.log(`✓ Added Software Renewal subscription to cart request:`, {
        variantId: this.softwareRenewal.variantId,
        sellingPlanId: sellingPlanId,
        planYears: this.selectedPlanYears
      });
    } else {
      console.warn(`⚠️ No selling plan found for ${this.selectedPlanYears} year plan`);
    }

    console.log('Cart request payload:', items);
    console.log(`Total items to add: ${items.length}`);

    // Add all items in a single request
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items })
    });

    console.log('Cart API response status:', response.status);
    console.log('Cart API response OK:', response.ok);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Cart API error response:', errorData);
      if (response.status === 422 && errorData.message?.includes('maximum quantity')) {
        throw new Error('QUANTITY_LIMIT');
      }

      throw new Error('Failed to add items to cart');
    }

    const result = await response.json();
    console.log('✓ Cart API success response:', result);
    return result;
  }
}

// Initialize when script loads
console.log('Iris Plan Selector script loaded');
new IrisPlanSelector();



// /**
//  * Iris Plan Selector
//  * Replaces variant selector with digital plan selection
//  * Adds both physical device and digital plan to cart separately
//  * Automatically includes extended warranty for 2 and 3 year plans
//  */

// class IrisPlanSelector {
//   constructor() {
//     console.log('=== Iris Plan Selector Initialized ===');
//     this.selectedPlanVariantId = null;
//     this.selectedPlanYears = null;
//     this.physicalProductVariantId = null;
//     this.plans = [];
//     this.warranties = [];
//     this.collectionHandle = 'exo-iris-plans';
//     this.warrantyCollectionHandle = 'warranty';
//     this.init();
//   }

//   async init() {
//     console.log('Init called, document ready state:', document.readyState);
//     // Wait for DOM to be ready
//     if (document.readyState === 'loading') {
//       document.addEventListener('DOMContentLoaded', () => this.setup());
//     } else {
//       this.setup();
//     }
//   }

//   async setup() {
//     console.log('Setup started');
    
//     // Check if we're on a product page - try multiple selectors
//     let productForm = document.querySelector('form[action*="/cart/add"]');
    
//     // If not found, try to find product-form element
//     if (!productForm) {
//       const productFormElement = document.querySelector('product-form');
//       if (productFormElement) {
//         productForm = productFormElement.querySelector('form');
//       }
//     }
    
//     console.log('Product form found:', !!productForm);
//     console.log('Product form element:', productForm);
    
//     if (!productForm) {
//       console.log('❌ Not on a product page - exiting');
//       return;
//     }

//     // Get the physical product variant ID (first available variant)
//     const variantIdInput = productForm.querySelector('[name="id"]');
//     console.log('Variant ID input found:', !!variantIdInput);
//     if (variantIdInput) {
//       this.physicalProductVariantId = variantIdInput.value;
//       console.log('✓ Physical product variant ID:', this.physicalProductVariantId);
//     }

//     // Find variant picker location
//     const variantPicker = document.querySelector('.variant-picker, variant-selects, .product-form__input--dropdown');
//     console.log('Variant picker found:', !!variantPicker);
//     if (!variantPicker) {
//       console.log('❌ Variant picker not found - exiting');
//       return;
//     }

//     console.log('✓ Creating plan selector UI');

//     // Hide the price element (usually shows $3,799.00)
//     const priceElements = document.querySelectorAll('.price, .product__info-wrapper .price, .price--large');
//     priceElements.forEach(priceEl => {
//       console.log('Hiding price element:', priceEl);
//       priceEl.style.display = 'none';
//     });

//     // Hide any Plan variant selector fieldsets or radio buttons
//     const fieldsets = document.querySelectorAll('fieldset, .product-form__input');
//     fieldsets.forEach(fieldset => {
//       const legend = fieldset.querySelector('legend');
//       if (legend && legend.textContent.toLowerCase().includes('plan')) {
//         console.log('Hiding plan fieldset:', fieldset);
//         fieldset.style.display = 'none';
//       }
//     });

//     // Create wrapper
//     const wrapper = document.createElement('div');
//     wrapper.className = 'iris-plan-selector-wrapper';
//     variantPicker.parentNode.insertBefore(wrapper, variantPicker);
//     wrapper.appendChild(variantPicker);

//     // Create plans container
//     const plansContainer = document.createElement('div');
//     plansContainer.className = 'iris-plans-container';
//     plansContainer.innerHTML = '<div class="iris-plan-loading">Loading plans...</div>';
//     wrapper.insertBefore(plansContainer, variantPicker);

//     // Create error message container
//     const errorContainer = document.createElement('div');
//     errorContainer.className = 'iris-plan-error';
//     errorContainer.textContent = 'Please select a plan before adding to cart';
//     wrapper.appendChild(errorContainer);

//     console.log('✓ UI elements created, now fetching plans and warranties...');

//     // Fetch plans and warranties
//     await this.fetchWarranties();
//     await this.fetchPlans();
//     this.renderPlans(plansContainer);

//     // Intercept add to cart button
//     this.interceptAddToCart(productForm, errorContainer);
    
//     console.log('=== Setup Complete ===');
//   }

//   async fetchWarranties() {
//     const url = `/collections/${this.warrantyCollectionHandle}/products.json`;
//     console.log('Fetching warranties from:', url);
    
//     try {
//       const response = await fetch(url);
//       console.log('Warranty response status:', response.status);
//       console.log('Warranty response OK:', response.ok);
      
//       if (!response.ok) {
//         console.error('❌ Failed to fetch warranty collection. Status:', response.status);
//         const text = await response.text();
//         console.error('Response text:', text);
//         return;
//       }

//       const data = await response.json();
//       console.log('✓ Warranty collection data received:', data);
//       console.log('Number of products in the warranty collection:', data.products?.length || 0);
      
//       if (!data.products || data.products.length === 0) {
//         console.error('❌ No products found in the warranty collection');
//         return;
//       }

//       // Map warranty products
//       this.warranties = data.products.map(product => {
//         const yearMatch = product.title.match(/(\d+)\s*year/i);
//         const years = yearMatch ? parseInt(yearMatch[1]) : 0;
        
//         const warranty = {
//           id: product.id,
//           title: product.title,
//           years: years,
//           price: parseFloat(product.variants[0].price),
//           variantId: product.variants[0].id,
//           handle: product.handle
//         };
        
//         console.log('Mapped warranty:', warranty);
//         return warranty;
//       });
      
//       console.log('✓ Total warranties processed:', this.warranties.length);

//     } catch (error) {
//       console.error('❌ Error fetching warranties:', error);
//       console.error('Error details:', error.message, error.stack);
//     }
//   }

//   async fetchPlans() {
//     const url = `/collections/${this.collectionHandle}/products.json`;
//     console.log('Fetching plans from:', url);
    
//     try {
//       const response = await fetch(url);
//       console.log('Plans response status:', response.status);
//       console.log('Plans response OK:', response.ok);
      
//       if (!response.ok) {
//         console.error('❌ Failed to fetch plans collection. Status:', response.status);
//         const text = await response.text();
//         console.error('Response text:', text);
//         return;
//       }
      
//       const data = await response.json();
//       console.log('✓ Plans collection data received:', data);
//       console.log('Number of products in collection:', data.products?.length || 0);
      
//       if (!data.products || data.products.length === 0) {
//         console.error('❌ No products found in collection');
//         return;
//       }

//       // Log each product
//       data.products.forEach((product, index) => {
//         console.log(`Plan Product ${index + 1}:`, {
//           title: product.title,
//           variants: product.variants?.length,
//           firstVariantPrice: product.variants?.[0]?.price,
//           firstVariantId: product.variants?.[0]?.id
//         });
//       });
      
//       // Sort plans by price (1 year, 2 year, 3 year)
//       this.plans = data.products
//         .map(product => {
//           const mapped = {
//             id: product.id,
//             title: product.title,
//             price: product.variants[0].price,
//             variantId: product.variants[0].id,
//             handle: product.handle
//           };
//           console.log('Mapped plan:', mapped);
//           return mapped;
//         })
//         .sort((a, b) => {
//           const priceA = parseFloat(a.price);
//           const priceB = parseFloat(b.price);
//           console.log(`Sorting: ${a.title} ($${priceA}) vs ${b.title} ($${priceB})`);
//           return priceA - priceB;
//         });
      
//       console.log('✓ Final plans array:', this.plans);
//       console.log(`✓ Total plans processed: ${this.plans.length}`);

//     } catch (error) {
//       console.error('❌ Error fetching plans:', error);
//       console.error('Error details:', error.message, error.stack);
//     }
//   }

//   renderPlans(container) {
//     console.log('renderPlans called with', this.plans.length, 'plans');
    
//     if (this.plans.length === 0) {
//       console.error('❌ No plans to render');
//       container.innerHTML = `
//         <div class="iris-plan-loading" style="color: #dc2626;">
//           <p>Unable to load plans.</p>
//           <p style="font-size: 12px; color: #6b7280;">Check browser console (F12) for details</p>
//         </div>
//       `;
//       return;
//     }

//     console.log('✓ Rendering plans...');
//     container.innerHTML = '';

//     this.plans.forEach((plan, index) => {
//       console.log(`Creating plan element ${index + 1}:`, plan);
//       const planElement = this.createPlanElement(plan, index);
//       container.appendChild(planElement);
//     });

//     console.log('✓ All plan elements added to DOM');

//     // Auto-select first plan
//     const firstPlan = container.querySelector('.iris-plan-option');
//     if (firstPlan) {
//       console.log('✓ Auto-selecting first plan');
//       firstPlan.click();
//     } else {
//       console.error('❌ Could not find first plan to auto-select');
//     }
//   }

//   createPlanElement(plan, index) {
//     const planDiv = document.createElement('div');
//     planDiv.className = 'iris-plan-option';
//     planDiv.dataset.variantId = plan.variantId;
//     planDiv.dataset.planIndex = index;

//     // Determine plan details based on title
//     const yearMatch = plan.title.match(/(\d+)\s*year/i);
//     const years = yearMatch ? parseInt(yearMatch[1]) : index + 1;
    
//     // Store years on the element for later use
//     planDiv.dataset.years = years;
    
//     // Prices in Shopify API are strings in format "1200.00" (already in dollars)
//     const planPrice = parseFloat(plan.price);
//     const devicePrice = 3799;
    
//     // Add warranty price if applicable (2 or 3 years)
//     let warrantyPrice = 0;
//     if (years >= 2) {
//       const warranty = this.warranties.find(w => w.years === years);
//       if (warranty) {
//         warrantyPrice = warranty.price;
//         console.log(`Adding warranty for ${years} year plan: $${warrantyPrice}`);
//       } else {
//         console.warn(`⚠️ No ${years} year warranty found`);
//       }
//     }
    
//     const totalPrice = (planPrice + devicePrice + warrantyPrice).toLocaleString('en-US', {
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2
//     });

//     console.log(`Plan ${index + 1} details:`, {
//       title: plan.title,
//       years,
//       planPrice,
//       devicePrice,
//       warrantyPrice,
//       totalPrice,
//       variantId: plan.variantId
//     });

//     planDiv.innerHTML = `
//       <div class="iris-plan-checkmark"></div>
//       <div class="iris-plan-header">
//         <h3 class="iris-plan-name">${years} Year Plan</h3>
//         <div class="iris-plan-price">$${totalPrice}</div>
//       </div>
//       <div class="iris-plan-details">
//         <div class="iris-plan-detail-item">${years} year warranty</div>
//         <div class="iris-plan-detail-item">${years} year${years > 1 ? 's' : ''} of AI updates</div>
//         <div class="iris-plan-detail-item">${years} year${years > 1 ? 's' : ''} of Exo Cloud Services</div>
//       </div>
//     `;

//     planDiv.addEventListener('click', () => this.selectPlan(planDiv));

//     return planDiv;
//   }

//   selectPlan(planElement) {
//     console.log('Plan selected:', planElement.dataset.variantId);
    
//     // Remove selection from all plans
//     document.querySelectorAll('.iris-plan-option').forEach(el => {
//       el.classList.remove('selected');
//     });

//     // Select clicked plan
//     planElement.classList.add('selected');
//     this.selectedPlanVariantId = planElement.dataset.variantId;
//     this.selectedPlanYears = parseInt(planElement.dataset.years);
    
//     console.log('✓ Selected plan variant ID:', this.selectedPlanVariantId);
//     console.log('✓ Selected plan years:', this.selectedPlanYears);

//     // Hide error message
//     const errorContainer = document.querySelector('.iris-plan-error');
//     if (errorContainer) {
//       errorContainer.classList.remove('show');
//     }
//   }

//   interceptAddToCart(form, errorContainer) {
//     console.log('Setting up add to cart interception');
//     console.log('Form element:', form);
    
//     // Try multiple ways to find the add to cart button
//     let addToCartBtn = form.querySelector('[name="add"]') || 
//                        form.querySelector('button[type="submit"]') ||
//                        form.querySelector('.product-form__submit') ||
//                        document.querySelector('.product-form__submit') ||
//                        document.querySelector('button[name="add"]');
    
//     console.log('Add to cart button found:', !!addToCartBtn);
//     console.log('Button element:', addToCartBtn);
    
//     if (!addToCartBtn) {
//       console.error('❌ Could not find add to cart button');
//       console.log('Available buttons in form:', form.querySelectorAll('button'));
//       return;
//     }

//     form.onsubmit = async (e) => {
//       console.log('=== Add to Cart Clicked ===');
//       console.log('Event:', e);
//       e.preventDefault();
//       e.stopPropagation();
//       e.stopImmediatePropagation();

//       console.log('Selected plan variant ID:', this.selectedPlanVariantId);
//       console.log('Selected plan years:', this.selectedPlanYears);
//       console.log('Physical product variant ID:', this.physicalProductVariantId);

//       // Check if plan is selected
//       if (!this.selectedPlanVariantId) {
//         console.error('❌ No plan selected');
//         errorContainer.classList.add('show');
//         errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
//         return false;
//       }

//       // Disable button and show loading state
//       const originalBtnText = addToCartBtn.textContent;
//       addToCartBtn.disabled = true;
//       addToCartBtn.textContent = 'Adding...';
//       console.log('Button disabled, showing loading state');

//       try {
//         console.log('Attempting to add items to cart...');
//         await this.addToCart();

//         console.log('✓ Successfully added to cart');
//         addToCartBtn.textContent = 'Added!';
        
//         // Trigger cart drawer/page refresh
//         console.log('Redirecting to cart...');
//         setTimeout(() => {
//           window.location.href = '/cart';
//         }, 500);

//       } catch (error) {
//         console.error('❌ Error adding to cart:', error);
//         console.error('Error details:', error.message, error.stack);
//         alert('There was an error adding items to cart. Please try again.');
//         addToCartBtn.disabled = false;
//         addToCartBtn.textContent = originalBtnText;
//       }

//       return false;
//     };
    
//     // Also add click event listener to button as backup
//     addToCartBtn.addEventListener('click', async (e) => {
//       console.log('=== Button Click Intercepted ===');
//       e.preventDefault();
//       e.stopPropagation();
//       e.stopImmediatePropagation();
      
//       // Trigger the form submit handler
//       if (form.onsubmit) {
//         form.onsubmit(e);
//       }
//     }, true); // Use capture phase
    
//     console.log('✓ Add to cart interception set up');
//   }

//   async addToCart() {
//     const items = [];

//     // 1. Add physical device
//     if (this.physicalProductVariantId) {
//       items.push({
//         id: this.physicalProductVariantId,
//         quantity: 1
//       });
//       console.log('✓ Added physical device to cart request:', this.physicalProductVariantId);
//     }

//     // 2. Add digital plan
//     if (this.selectedPlanVariantId) {
//       items.push({
//         id: this.selectedPlanVariantId,
//         quantity: 1
//       });
//       console.log('✓ Added digital plan to cart request:', this.selectedPlanVariantId);
//     }

//     // 3. Add warranty if 2 or 3 year plan
//     if (this.selectedPlanYears >= 2) {
//       const warranty = this.warranties.find(w => w.years === this.selectedPlanYears);
//       if (warranty) {
//         items.push({
//           id: warranty.variantId,
//           quantity: 1
//         });
//         console.log(`✓ Added ${this.selectedPlanYears} year warranty to cart request:`, warranty.variantId, `($${warranty.price})`);
//       } else {
//         console.warn(`⚠️ No warranty found for ${this.selectedPlanYears} year plan`);
//       }
//     }

//     console.log('Cart request payload:', items);
//     console.log(`Total items to add: ${items.length}`);

//     // Add all items in a single request
//     const response = await fetch('/cart/add.js', {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify({ items })
//     });

//     console.log('Cart API response status:', response.status);
//     console.log('Cart API response OK:', response.ok);

//     if (!response.ok) {
//       const errorData = await response.json();
//       console.error('❌ Cart API error response:', errorData);
//       throw new Error('Failed to add items to cart');
//     }

//     const result = await response.json();
//     console.log('✓ Cart API success response:', result);
//     return result;
//   }
// }

// // Initialize when script loads
// console.log('Iris Plan Selector script loaded');
// new IrisPlanSelector();


